import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback, useState } from "react";
import { useFullSubmit } from "src/apps/AppFlow/pages/hooks";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { AgreementParagraph, CreditAuthorization, FormFlowPage, InfoAlert } from "src/components";
import DownloadButton from "src/components/DownloadButton";
import IllustrativeOption from "src/components/IllustrativeOptions/IllustrativeOption";
import config from "src/config";
import { selectApplicationLocation } from "src/selectors";
import { createStyles } from "src/styles";
import { downloadFile } from "src/utilities/file/download";
import { BlockFocus } from "src/utilities/react";

import { debounce, enums, types } from "@fraction/shared";

export interface ImportantTermsAndInfoNoPdfViewProps {
  focusRef?: Ref<HTMLDivElement>;
  blockFocus?: BlockFocus;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  value: ApplicationFlowState;
  children?: ReactNode;
  location?: types.ExtractedPropertyAddress;
}

const styles = createStyles({
  container: {
    paddingBottom: 160,
  },
  alert: {
    marginBottom: 40,
  },
  agreement: {
    marginBottom: 20,
  },
  anchor: { textDecoration: "none" },
  optionContainer: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    marginBottom: 24,
  },
  leftOption: {
    marginRight: 16,
  },
});

const FILES = [
  {
    href: `${config.s3URL.legalDocs}/WA/Heloc-booklet.pdf`,
    filename: "fraction-heloc-booklet.pdf",
  },
  {
    href: "https://api.fraction.com/document/dl/tila-important-terms-18mo/washington",
    filename: "fraction-tila-terms.pdf",
  },
];

const doNothing = () => {};

const downloadBooklet = debounce(() => downloadFile(FILES[0].href, FILES[0].filename), 2000, {
  leading: true,
});
const downloadTila = debounce(() => downloadFile(FILES[1].href, FILES[1].filename), 2000, { leading: true });

const ImportantTermsAndInfoNoPdfView = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  children,
  location,
  ...props
}: ImportantTermsAndInfoNoPdfViewProps) => {
  const [filesToDownload, setFilesToDownload] = useState(FILES);
  const { handleNext, nextDisabled, nextLoading } = useFullSubmit();
  const { isColorado } = selectApplicationLocation(location);

  const handleClickHelocBooklet = useCallback(async () => {
    await downloadBooklet();
    setFilesToDownload((prev) => prev.filter((file) => file.href !== FILES[0].href));
  }, []);

  const handleClickTILA = useCallback(async () => {
    await downloadTila();
    setFilesToDownload((prev) => prev.filter((file) => file.href !== FILES[1].href));
  }, []);

  const onDownloadAll = useCallback(() => {
    setFilesToDownload([]);
    handleNext?.();
  }, [handleNext]);

  const bookletDownloaded = !filesToDownload.find((file) => file.href === FILES[0].href);
  const importantTermsDownloaded = !filesToDownload.find((file) => file.href === FILES[1].href);

  return (
    <FormFlowPage
      {...props}
      title="Important terms and information"
      description="Please download the following informational booklets in order to further review details about the loan you are applying for."
      buttonText="Submit application"
      overflowTextFallback="Submit"
      // do nothing because we only want to go next once the download is complete, not when the button is clicked
      onNext={doNothing}
      onBack={onBack}
      style={[styles.container, style]}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      ref={focusRef}
      isStickyDesktopFooter
      button={
        <DownloadButton onDownload={onDownloadAll} files={filesToDownload}>
          {filesToDownload.length > 0 ? "Download and continue" : "Next"}
        </DownloadButton>
      }
    >
      <div css={styles.optionContainer}>
        <IllustrativeOption
          disabled={bookletDownloaded}
          value={bookletDownloaded ? "checkmark" : enums.DebtType.PERSONAL_LOAN}
          label="HELOC booklet"
          onClick={handleClickHelocBooklet}
          style={styles.leftOption}
        />
        <IllustrativeOption
          disabled={importantTermsDownloaded}
          value={
            importantTermsDownloaded ? "checkmark" : enums.FinancialAssetType.SHARES_OR_OTHER_INVESTMENTS
          }
          label="Important terms"
          onClick={handleClickTILA}
        />
      </div>
      {isColorado && (
        <InfoAlert style={styles.alert}>
          An additional charge may be made for insurance written in connection with your loan transaction
          against loss of or damage to property or against liability. You may choose the person through whom
          the insurance is to be obtained.
        </InfoAlert>
      )}
      <AgreementParagraph style={styles.agreement}>
        <CreditAuthorization />
      </AgreementParagraph>
      {children}
    </FormFlowPage>
  );
};

export default memo(ImportantTermsAndInfoNoPdfView);
